import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, TextInput, Textarea, Button, Stack, Group, Text, SegmentedControl, JsonInput } from '@mantine/core';
import type { BotMcpService } from '../../apis';

export interface McpSettingsDialogProps {
  opened: boolean;
  onClose: () => void;
  editingService?: BotMcpService | null;
  onSave: (service: {
    name: string;
    description: string;
    available_tools: string;
    jsonConfig: string;
    type: number;
  }) => Promise<boolean> | boolean;
}

// 配置类型常量
const CONFIG_TYPE = {
  MCP_URL: 1,
  JSON_CONFIG: 2,
} as const;

type ConfigType = (typeof CONFIG_TYPE)[keyof typeof CONFIG_TYPE];

const defaultMcpUrl = '';
const defaultJsonConfig = '';
const jsonConfigPlaceholder = `{
  "command": "node",
  "args": ["/path/to/server.js"],
  "env": {}
}`;

export function McpSettingsDialog({ opened, onClose, editingService, onSave }: McpSettingsDialogProps) {
  const { t } = useTranslation();
  const [configType, setConfigType] = useState<ConfigType>(CONFIG_TYPE.MCP_URL);
  const [mcpName, setMcpName] = useState('');
  const [description, setDescription] = useState('');
  const [tools, setTools] = useState('');
  const [mcpUrl, setMcpUrl] = useState(defaultMcpUrl);
  const [jsonConfig, setJsonConfig] = useState(defaultJsonConfig);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // 初始化表单数据
  useEffect(() => {
    if (opened) {
      if (editingService) {
        setMcpName(editingService.name);
        setDescription(editingService.description);
        setTools(editingService.available_tools);

        // 根据现有数据判断配置类型
        try {
          const configStr =
            typeof editingService.json_config === 'string'
              ? editingService.json_config
              : JSON.stringify(editingService.json_config || {});
          const config = JSON.parse(configStr);

          if (config.url && typeof config.url === 'string' && Object.keys(config).length === 1) {
            // 如果配置中只有url字段，判断为MCP URL类型
            setConfigType(CONFIG_TYPE.MCP_URL);
            setMcpUrl(config.url);
            setJsonConfig(defaultJsonConfig);
          } else {
            // 否则判断为JSON配置类型
            setConfigType(CONFIG_TYPE.JSON_CONFIG);
            setMcpUrl(defaultMcpUrl);
            setJsonConfig(configStr);
          }
        } catch {
          // 解析失败时默认为MCP URL类型
          setConfigType(CONFIG_TYPE.MCP_URL);
          setMcpUrl(defaultMcpUrl);
          setJsonConfig(defaultJsonConfig);
        }
      } else {
        // 新建时的默认值
        setConfigType(CONFIG_TYPE.MCP_URL);
        setMcpName('');
        setDescription('');
        setTools('');
        setMcpUrl(defaultMcpUrl);
        setJsonConfig(defaultJsonConfig);
      }
      setErrors({});
    }
  }, [opened, editingService]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!mcpName.trim()) {
      newErrors.name = t('mcp.dialog.validation.name_required');
    }

    if (configType === CONFIG_TYPE.MCP_URL) {
      // MCP URL类型的校验
      if (!mcpUrl.trim()) {
        newErrors.mcpUrl = t('mcp.dialog.validation.url_required');
      } else {
        try {
          new URL(mcpUrl);
        } catch {
          newErrors.mcpUrl = t('mcp.dialog.validation.url_invalid');
        }
      }
    } else {
      // JSON配置类型的校验 - 只校验JSON格式，不限制内容结构
      if (!jsonConfig.trim()) {
        newErrors.jsonConfig = t('mcp.dialog.validation.json_required');
      } else {
        try {
          JSON.parse(jsonConfig);
          // JSON格式有效，不对内容结构做进一步限制
        } catch {
          newErrors.jsonConfig = t('mcp.dialog.validation.json_invalid');
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    let finalJsonConfig: string;

    if (configType === CONFIG_TYPE.MCP_URL) {
      // MCP URL类型：将URL包装为JSON配置
      finalJsonConfig = JSON.stringify({ url: mcpUrl.trim() });
    } else {
      // JSON配置类型：直接使用JSON配置
      finalJsonConfig = jsonConfig.trim();
    }

    // 等待保存结果再决定是否关闭：失败时保持弹窗打开、表单值不丢失，便于用户修正重试
    const ok = await onSave({
      name: mcpName.trim(),
      description: description.trim(),
      available_tools: tools.trim(),
      jsonConfig: finalJsonConfig,
      type: configType,
    });

    if (ok) {
      handleClose();
    }
  };

  const handleClose = () => {
    // 重置表单
    setConfigType(CONFIG_TYPE.MCP_URL);
    setMcpName('');
    setDescription('');
    setTools('');
    setMcpUrl(defaultMcpUrl);
    setJsonConfig(defaultJsonConfig);
    setErrors({});
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={600} size="lg">
          {editingService ? t('mcp.dialog.edit_title') : t('mcp.dialog.add_title')}
        </Text>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('mcp.dialog.description')}
        </Text>

        <TextInput
          label={t('mcp.dialog.name_label')}
          placeholder={t('mcp.dialog.name_placeholder')}
          value={mcpName}
          onChange={(e) => setMcpName(e.target.value)}
          error={errors.name}
          maxLength={100}
          required
        />

        <Textarea
          label={t('mcp.dialog.description_label')}
          placeholder={t('mcp.dialog.description_placeholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          minRows={3}
        />

        <Textarea
          label={t('mcp.dialog.tools_label')}
          placeholder={t('mcp.dialog.tools_placeholder')}
          value={tools}
          onChange={(e) => setTools(e.target.value)}
          minRows={2}
        />

        {/* 配置类型切换 */}
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            {t('mcp.dialog.config_type_label')}
          </Text>
          <SegmentedControl
            value={configType.toString()}
            onChange={(value) => {
              setConfigType(parseInt(value) as ConfigType);
              setErrors({}); // 清除错误信息
            }}
            data={[
              { label: t('mcp.dialog.mcp_url_label'), value: CONFIG_TYPE.MCP_URL.toString() },
              { label: t('mcp.dialog.json_config_label'), value: CONFIG_TYPE.JSON_CONFIG.toString() },
            ]}
            fullWidth
          />
        </Stack>

        {/* 根据配置类型显示不同的输入框 */}
        {configType === CONFIG_TYPE.MCP_URL ? (
          <TextInput
            label={t('mcp.dialog.url_label')}
            placeholder={t('mcp.dialog.url_placeholder')}
            value={mcpUrl}
            onChange={(e) => setMcpUrl(e.target.value)}
            error={errors.mcpUrl}
            required
            description={t('mcp.dialog.url_description')}
          />
        ) : (
          <JsonInput
            label={t('mcp.dialog.json_config_label')}
            placeholder={jsonConfigPlaceholder}
            value={jsonConfig}
            onChange={setJsonConfig}
            error={errors.jsonConfig}
            required
            minRows={8}
            maxRows={15}
            description={t('mcp.dialog.json_config_description')}
            formatOnBlur
            autosize
          />
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="outline" onClick={handleClose}>
            {t('mcp.dialog.cancel')}
          </Button>
          <Button onClick={handleSave}>{editingService ? t('mcp.dialog.save') : t('mcp.dialog.add')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
